import type { DropResult } from '@hello-pangea/dnd';
import is from '@sindresorhus/is';
import { useCallback, useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useStore } from 'zustand';

import type { Competencies, JobProfileComponent, SectionTagDefinition } from '~/shared/data-contract/job-profile';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { JobProfileSkill } from '~/shared/data-contract/skill';

import type { JobItemDetails } from '../api/use-job-item-details';
import { useDraftFormSync } from '../hooks/use-draft-form-sync';
import { useHasFormChanges } from '../hooks/use-has-form-changes';
import { useDraftVersionStore } from './DraftVersionStoreProvider';
import { useManualEditStore } from './ManualEditStoreProvider';
import { addSkillsToCompetencies, useSkillsWithCompetencies } from './skills/use-skills-with-compentencies';
import type { CompetencyWithSkills, ProfileForm } from './types';
import { useBeforeUnload } from './use-before-unload';
import { type SaveJobProfileOptions, useJobProfileFormActions } from './use-job-profile-form-actions';
import { useSkillActionStore } from './use-skill-action-store';
import { useUnsavedChangesGuard } from './use-unsaved-changes-guard';

export type UseJobProfileFormParams = {
    readonly architectureId: string;
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly onSkillProficiencyChanged?: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly parentRoleId?: string;
    readonly profile: JobProfileComponent;
    readonly regeneratedSectionTags?: readonly SectionTagDefinition[];
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

export type UseJobProfileFormResult = {
    readonly allCompetencies: Competencies | undefined;
    readonly competenciesWithSkills: CompetencyWithSkills[];
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly formSkills: readonly JobProfileSkill[];
    readonly handleCancel: () => void;
    readonly handleCancelDiscard: () => void;
    readonly handleConfirmDiscard: () => void;
    readonly handleManualEdit: () => void;
    readonly handleMove: (instructions: DropResult<string>) => void;
    readonly handleNewVersion: () => void;
    readonly handleRemoveCompetency: (competencyId: string) => void;
    readonly handleSaveSubmit: (data: ProfileForm, options: SaveJobProfileOptions) => Promise<void>;
    readonly handleSaveValidationError: () => void;
    readonly hasFormChanges: boolean;
    readonly isDiscardDialogOpen: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly removeSkill: (competencyId: string, skillId: string) => void;
    readonly versions: JobProfileVersions | undefined;
};

export const useJobProfileForm = ({
    architectureId,
    isEditing,
    item,
    itemId,
    itemType,
    onSkillProficiencyChanged,
    parentRoleId,
    profile,
    regeneratedSectionTags,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}: UseJobProfileFormParams): UseJobProfileFormResult => {
    const setHasManualEdits = useManualEditStore((state) => state.setHasManualEdits);
    const handleManualEdit = useCallback(() => {
        setHasManualEdits(true);
    }, [setHasManualEdits]);

    const draftStore = useDraftVersionStore();
    const resetSkillActionStore = useSkillActionStore((state) => state.reset);
    const hasDraft = useStore(draftStore, (state) => state.drafts.has(itemId));

    const formMethods = useForm<ProfileForm>({
        defaultValues: {
            competencies: addSkillsToCompetencies([...(profile.competencies?.values() ?? [])], profile.skills ?? []),
            skills: profile.skills ?? [],
            translations: profile.translations,
            versionNote: undefined,
        },
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        if (isEditing) {
            return;
        }
        const itemVersion = item.versionInfo?.version?.toString();
        if (selectedVersion !== undefined && itemVersion !== undefined && itemVersion !== selectedVersion) {
            return;
        }
        formMethods.reset({
            competencies: addSkillsToCompetencies([...(profile.competencies?.values() ?? [])], profile.skills ?? []),
            skills: profile.skills ?? [],
            translations: profile.translations,
            versionNote: undefined,
        });
    }, [
        isEditing,
        selectedVersion,
        item.profileGenerationInfo?.updatedAt,
        item.versionInfo?.version,
        item.profileGenerationInfo?.skillDetectionState,
        item.profileGenerationInfo?.translationState,
    ]);

    useDraftFormSync({ draftStore, formMethods, hasDraft, isEditing, itemId });

    useBeforeUnload({ shouldPreventUnload: isEditing });

    const hasFormChanges = useHasFormChanges({ isEditing });

    const { cancelDiscard, confirmDiscard, isDiscardDialogOpen, requestDiscard } = useUnsavedChangesGuard({
        hasUnsavedChanges: hasFormChanges,
    });

    const resetForm = useCallback(() => {
        setIsEditing(false);
        resetSkillActionStore();
        draftStore.getState().removeDraft(itemId);
        formMethods.reset();
    }, [setIsEditing, resetSkillActionStore, draftStore, itemId, formMethods]);

    const handleCancel = useCallback(() => {
        if (hasFormChanges) {
            requestDiscard(resetForm);
            return;
        }
        resetForm();
    }, [hasFormChanges, requestDiscard, resetForm]);

    const { allCompetencies, formSkills, handleNewVersion, handleSaveSubmit, handleSaveValidationError, versions } =
        useJobProfileFormActions({
            architectureId,
            draftStore,
            formMethods,
            hasDraft,
            item,
            itemId,
            itemType,
            parentRoleId,
            profile,
            regeneratedSectionTags,
            resetSkillActionStore,
            selectedVersion,
            setActiveVersion,
            setIsEditing,
        });

    const { competenciesWithSkills, move, removeCompetency, removeSkill } = useSkillsWithCompetencies({
        formMethods,
    });

    const handleMove = useCallback(
        (...args: Parameters<typeof move>) => {
            handleManualEdit();
            move(...args);
        },
        [handleManualEdit, move],
    );

    const handleRemoveCompetency = useCallback(
        (competencyId: string) => {
            handleManualEdit();
            removeCompetency(competencyId);
        },
        [handleManualEdit, removeCompetency],
    );

    const handleRemoveSkill = useCallback(
        (competencyId: string, skillId: string) => {
            handleManualEdit();
            removeSkill(competencyId, skillId);
        },
        [handleManualEdit, removeSkill],
    );

    const onChangeProficiencyLevel = useCallback(
        (skillId: string, level: JobProfileSkill['level']) => {
            handleManualEdit();
            formMethods.setValue(
                'skills',
                formMethods.getValues('skills').map((skill) => ({
                    ...skill,
                    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
                    level: skill.id === skillId ? (level as any) : skill.level,
                })),
            );
            if (!is.nullOrUndefined(level)) {
                onSkillProficiencyChanged?.(skillId, level);
            }
        },
        [formMethods, handleManualEdit, onSkillProficiencyChanged],
    );

    return {
        allCompetencies,
        competenciesWithSkills,
        formMethods,
        formSkills,
        handleCancel,
        handleCancelDiscard: cancelDiscard,
        handleConfirmDiscard: confirmDiscard,
        handleManualEdit,
        handleMove,
        handleNewVersion,
        handleRemoveCompetency,
        handleSaveSubmit,
        handleSaveValidationError,
        hasFormChanges,
        isDiscardDialogOpen,
        onChangeProficiencyLevel,
        removeSkill: handleRemoveSkill,
        versions,
    };
};
